import { useState, useCallback, useEffect, useMemo } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addDays, subDays } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

import { useAdmin } from '@/contexts/AdminContext';
import { useBookingDraft } from '@/contexts/BookingDraftContext';
import BookingDetail from '@/components/admin/BookingDetail';

import useCalendarEvents from '@/hooks/useCalendarEvents';
import useProviderSchedules from '@/hooks/useProviderSchedules';

import CalendarToolbar from '@/components/admin/calendar/CalendarToolbar';
import { CalendarEvent, CalendarMonthEvent } from '@/components/admin/calendar/CalendarEvent';
import QuickBookingPopover from '@/components/admin/calendar/QuickBookingPopover';

// Styles
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import '@/components/admin/calendar/calendarStyles.css';

// ── Localizer ──
const locales = { 'en-GB': enGB };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

// ── DnD Calendar ──
const DnDCalendar = withDragAndDrop(BigCalendar);

// ── Media query hook ──
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function Calendar() {
  const { siteAccess, apiFetch } = useAdmin();
  const { createDraft } = useBookingDraft();
  const isMobile = useMediaQuery('(max-width: 767px)');

  // ── State ──
  const [currentView, setCurrentView] = useState(() => {
    const saved = localStorage.getItem('cal-view');
    if (saved && ['day', 'week', 'month'].includes(saved)) return saved;
    return 'week';
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeSites, setActiveSites] = useState(() => [...(siteAccess || [])]);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [quickBookSlot, setQuickBookSlot] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);

  // ── Date range for fetching ──
  const { startDate, endDate } = useMemo(() => {
    const month = startOfMonth(currentDate);
    // Fetch a wider range to cover week view edges
    return {
      startDate: subDays(month, 7),
      endDate: addDays(endOfMonth(currentDate), 7),
    };
  }, [currentDate]);

  // ── Custom hooks ──
  const {
    events, loading, refetch,
    optimisticMove, optimisticResize,
    todayCount, nextAppointment,
  } = useCalendarEvents({ startDate, endDate, siteFilter: activeSites });

  const { providers, scheduleMap } = useProviderSchedules({ siteFilter: activeSites });

  // ── Use mobile-friendly defaults ──
  useEffect(() => {
    if (isMobile && currentView === 'week') {
      setCurrentView('day');
    }
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Check push permission on mount ──
  useEffect(() => {
    if ('Notification' in window) {
      setPushEnabled(Notification.permission === 'granted');
    }
  }, []);

  // ── View persistence ──
  useEffect(() => {
    localStorage.setItem('cal-view', currentView);
  }, [currentView]);

  // ── Handlers ──
  const handleNavigate = useCallback((newDate) => {
    setCurrentDate(newDate);
  }, []);

  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
  }, []);

  const handleRangeChange = useCallback(() => {
    // Date range is computed from currentDate, so nothing extra needed here.
    // The useCalendarEvents hook will refetch when startDate/endDate change.
  }, []);

  // Drag → reschedule
  const handleEventDrop = useCallback(({ event, start, end }) => {
    if (event.data?.type !== 'booking') {
      toast.error('Course events cannot be moved');
      return;
    }
    optimisticMove(event.id, start, end);
  }, [optimisticMove]);

  // Resize → change duration
  const handleEventResize = useCallback(({ event, start, end }) => {
    if (event.data?.type !== 'booking') return;
    optimisticResize(event.id, start, end);
  }, [optimisticResize]);

  // Empty slot click → quick booking
  const handleSelectSlot = useCallback(({ start, end, action, resourceId }) => {
    if (action === 'doubleClick' || action === 'select' || action === 'click') {
      // Mobile month: tap day → switch to day view
      if (isMobile && currentView === 'month') {
        setCurrentView('day');
        setCurrentDate(start);
        return;
      }
      setQuickBookSlot({ start, end, resourceId });
    }
  }, [isMobile, currentView]);

  // Event click → booking detail
  const handleSelectEvent = useCallback((event) => {
    if (event.data?.type === 'booking' && event.data?.metadata?.bookingId) {
      setSelectedBookingId(event.data.metadata.bookingId);
    }
  }, []);

  // Site filter toggle
  const handleToggleSite = useCallback((siteKey) => {
    setActiveSites(prev => {
      if (prev.includes(siteKey)) {
        return prev.length === 1 ? prev : prev.filter(s => s !== siteKey);
      }
      return [...prev, siteKey];
    });
  }, []);

  // Provider filter toggle
  const handleToggleProvider = useCallback((providerId) => {
    setSelectedProviders(prev => {
      if (prev.includes(providerId)) {
        return prev.filter(id => id !== providerId);
      }
      return [...prev, providerId];
    });
  }, []);

  // Push notification subscribe
  const handleTogglePush = useCallback(async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }

    if (Notification.permission === 'granted') {
      toast.info('Notifications are already enabled');
      return;
    }

    if (Notification.permission === 'denied') {
      toast.error('Notifications are blocked. Please enable them in your browser settings.');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        return;
      }

      // Get service worker registration
      const reg = await navigator.serviceWorker.ready;

      // Get VAPID public key from env or a known constant
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        toast.error('Push notifications not configured');
        return;
      }

      // Subscribe
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      // Send subscription to backend
      const res = await apiFetch('/notifications/push/subscribe', {
        method: 'POST',
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')))),
          },
        }),
      });

      if (res.ok) {
        setPushEnabled(true);
        toast.success('Push notifications enabled!');
      } else {
        toast.error('Failed to register push subscription');
      }
    } catch (err) {
      console.error('[Push] subscribe error:', err);
      toast.error('Failed to enable notifications');
    }
  }, [apiFetch]);

  // Quick booking → create draft
  const handleCreateDraft = useCallback((draftData) => {
    createDraft({
      ...draftData,
      siteKey: activeSites.length === 1 ? activeSites[0] : '',
    });
    setQuickBookSlot(null);
  }, [createDraft, activeSites]);

  // ── Style getters ──
  const eventPropGetter = useCallback((event) => {
    if (event.allDay || event.data?.type === 'cohort') {
      const siteColors = {
        london_dental_academy: '#3B82F6',
      };
      return {
        style: {
          backgroundColor: siteColors[event.data?.siteKey] || '#6366f1',
          borderColor: 'transparent',
          color: '#fff',
          borderRadius: '6px',
        },
      };
    }
    return {};
  }, []);

  const slotPropGetter = useCallback((date) => {
    if (!scheduleMap.size || selectedProviders.length === 0) return {};
    const dayOfWeek = getDay(date);
    const timeStr = format(date, 'HH:mm');

    const anyAvailable = selectedProviders.some(pId => {
      const provSchedule = scheduleMap.get(pId);
      if (!provSchedule) return false;
      const daySchedule = provSchedule.get(dayOfWeek);
      if (!daySchedule || !daySchedule.isAvailable) return false;
      return daySchedule.timeSlots.has(timeStr);
    });

    if (!anyAvailable) return { className: 'rbc-unavailable' };
    return {};
  }, [scheduleMap, selectedProviders]);

  // ── Resource view (Day view — always show provider columns) ──
  const showResources = currentView === 'day' && providers.length > 0 && !isMobile;
  const filteredResources = useMemo(() => {
    if (!showResources) return undefined;
    // If providers are filtered, show only those; otherwise show all
    const list = selectedProviders.length > 0
      ? providers.filter(p => selectedProviders.includes(p.id))
      : providers;
    return list.map(p => ({ id: p.id, name: p.name }));
  }, [showResources, providers, selectedProviders]);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handler = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;

      switch (e.key.toLowerCase()) {
        case 't':
          setCurrentDate(new Date());
          break;
        case 'd':
          setCurrentView('day');
          break;
        case 'w':
          setCurrentView('week');
          break;
        case 'm':
          setCurrentView('month');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // ── Swipe navigation (mobile) ──
  useEffect(() => {
    if (!isMobile) return;

    let touchStartX = 0;
    let touchStartY = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      const deltaX = e.changedTouches[0].clientX - touchStartX;
      const deltaY = e.changedTouches[0].clientY - touchStartY;

      // Only trigger on horizontal swipes (> 50px horizontal, < 30px vertical)
      if (Math.abs(deltaX) > 50 && Math.abs(deltaY) < 30) {
        if (deltaX > 0) {
          // Swipe right → previous
          setCurrentDate(prev => {
            if (currentView === 'day') return subDays(prev, 1);
            if (currentView === 'week') return subDays(prev, 7);
            return subDays(prev, 30);
          });
        } else {
          // Swipe left → next
          setCurrentDate(prev => {
            if (currentView === 'day') return addDays(prev, 1);
            if (currentView === 'week') return addDays(prev, 7);
            return addDays(prev, 30);
          });
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, currentView]);

  // ── Custom toolbar wrapper ──
  const ToolbarComponent = useCallback((props) => (
    <CalendarToolbar
      {...props}
      activeSites={activeSites}
      onToggleSite={handleToggleSite}
      providers={providers}
      selectedProviders={selectedProviders}
      onToggleProvider={handleToggleProvider}
      todayCount={todayCount}
      nextAppointment={nextAppointment}
      pushEnabled={pushEnabled}
      onTogglePush={handleTogglePush}
      siteAccess={siteAccess}
    />
  ), [activeSites, handleToggleSite, providers, selectedProviders, handleToggleProvider, todayCount, nextAppointment, pushEnabled, handleTogglePush, siteAccess]);

  // ── Components config ──
  const calendarComponents = useMemo(() => ({
    toolbar: ToolbarComponent,
    event: CalendarEvent,
    month: { event: CalendarMonthEvent },
  }), [ToolbarComponent]);

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Calendar */}
      <div className="flex-1 min-h-0">
        {loading && events.length === 0 ? (
          <div className="flex flex-col gap-3 pb-4">
            <div className="flex justify-between items-center">
              <div>
                <div className="h-3 w-20 bg-stone-200 rounded animate-pulse" />
                <div className="h-8 w-36 bg-stone-200 rounded mt-2 animate-pulse" />
              </div>
              <div className="flex gap-2">
                <div className="h-9 w-16 bg-stone-200 rounded-lg animate-pulse" />
                <div className="h-9 w-16 bg-stone-200 rounded-lg animate-pulse" />
                <div className="h-9 w-16 bg-stone-200 rounded-lg animate-pulse" />
              </div>
            </div>
            <div className="h-10 bg-stone-200 rounded-xl animate-pulse" />
            <div className="flex-1 bg-stone-100 rounded-xl animate-pulse" style={{ minHeight: '500px' }} />
          </div>
        ) : (
          <DnDCalendar
            localizer={localizer}
            events={events}
            views={['month', 'week', 'day']}
            view={currentView}
            date={currentDate}
            step={15}
            timeslots={4}
            min={new Date(2000, 0, 1, 7, 0, 0)}
            max={new Date(2000, 0, 1, 21, 0, 0)}
            scrollToTime={new Date(2000, 0, 1, 8, 0, 0)}
            selectable
            resizable={!isMobile}
            draggableAccessor={(event) => !isMobile && event.data?.type === 'booking'}
            dayLayoutAlgorithm="overlap"
            popup
            showMultiDayTimes

            // Resources (Day view with multiple providers)
            resources={filteredResources}
            resourceIdAccessor="id"
            resourceTitleAccessor="name"

            // Custom components
            components={calendarComponents}

            // Style getters
            eventPropGetter={eventPropGetter}
            slotPropGetter={slotPropGetter}

            // Handlers
            onEventDrop={handleEventDrop}
            onEventResize={handleEventResize}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            onRangeChange={handleRangeChange}
          />
        )}
      </div>

      {/* Booking detail slide-over */}
      <AnimatePresence>
        {selectedBookingId && (
          <BookingDetail
            bookingId={selectedBookingId}
            onClose={() => setSelectedBookingId(null)}
            onUpdate={() => {
              refetch();
              setSelectedBookingId(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Quick booking popover/drawer */}
      {quickBookSlot && (
        <QuickBookingPopover
          slot={quickBookSlot}
          providers={providers}
          onCreateDraft={handleCreateDraft}
          onClose={() => setQuickBookSlot(null)}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}

// ── Helper: Convert VAPID key for Web Push ──
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
